
import { toast } from "@/hooks/use-toast";
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Info 
} from "lucide-react";

// Toast type definitions
type ToastType = "success" | "error" | "warning" | "info";

// Toast options interface
interface ToastOptions {
  title?: string;
  description: string;
  duration?: number;
}

// Define valid variant types for toast to match the expected string literals
type ToastVariant = "success" | "warning" | "info" | "default" | "destructive";

// Default durations for each toast type
const TOAST_DURATIONS = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 3000
};

/**
 * Create a standardized toast notification
 */
const createToast = (
  type: ToastType, 
  { title, description, duration }: ToastOptions
) => {
  const defaultTitle = {
    success: "Success",
    error: "Error",
    warning: "Warning",
    info: "Information"
  };

  const icons = {
    success: <CheckCircle className="h-5 w-5" />,
    error: <XCircle className="h-5 w-5" />,
    warning: <AlertTriangle className="h-5 w-5" />,
    info: <Info className="h-5 w-5" />
  };

  // Map toast types to valid variant names with explicit type mapping
  const variantMap: Record<ToastType, ToastVariant> = {
    success: "success",
    error: "destructive", // This is the key change - mapping 'error' to 'destructive' variant
    warning: "warning",
    info: "info"
  };

  return toast({
    title: title || defaultTitle[type],
    description,
    duration: duration || TOAST_DURATIONS[type],
    variant: variantMap[type],
    icon: icons[type],
  });
};

// Export specific toast functions with updated implementations
export const showSuccessToast = (options: ToastOptions) => 
  createToast("success", options);

export const showErrorToast = (options: ToastOptions) => 
  createToast("error", options);

export const showWarningToast = (options: ToastOptions) => 
  createToast("warning", options);

export const showInfoToast = (options: ToastOptions) => 
  createToast("info", options);

// Convenience function for form submission success
export const showFormSubmitSuccess = (entity: string) => 
  showSuccessToast({
    description: `${entity} submitted successfully.`
  });

// Convenience function for form submission errors
export const showFormSubmitError = (error: any) => 
  showErrorToast({
    description: error?.message || "An error occurred while submitting the form. Please try again."
  });

// Convenience function for data fetch errors
export const showFetchError = (error: any) => 
  showErrorToast({
    description: error?.message || "Failed to fetch data. Please try again."
  });

// Convenience function for save success
export const showSaveSuccess = (entity: string) => 
  showSuccessToast({
    description: `${entity} saved successfully.`
  });

// Convenience function for delete success
export const showDeleteSuccess = (entity: string) => 
  showSuccessToast({
    description: `${entity} deleted successfully.`
  });

// Convenience function for update success
export const showUpdateSuccess = (entity: string) => 
  showSuccessToast({
    description: `${entity} updated successfully.`
  });
